import { useEffect, useRef, useCallback } from "react";
import { getMillisecondsUntilNext11AMUTC } from "@/utils/scheduling";
import { getBuildInfo } from "@/utils/build-info";

const STORAGE_KEY = "app_version";

interface UseVersionMonitorOptions {
  enabled?: boolean;
}

export const useVersionMonitor = (options: UseVersionMonitorOptions = {}) => {
  const { enabled = true } = options;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getStoredVersion = useCallback((): string | null => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (error) {
      console.warn("[VersionMonitor] Failed to read from localStorage:", error);
      return null;
    }
  }, []);

  const setStoredVersion = useCallback((version: string): void => {
    try {
      localStorage.setItem(STORAGE_KEY, version);
    } catch (error) {
      console.warn("[VersionMonitor] Failed to read from localStorage:", error);
    }
  }, []);

  const refreshPage = useCallback((): void => {
    window.location.reload();
  }, []);

  const scheduleNextCheck = useCallback((): void => {
    if (!enabled) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const msUntilNext11AM = getMillisecondsUntilNext11AMUTC();

    timeoutRef.current = setTimeout(() => {
      checkVersion();
    }, msUntilNext11AM);
  }, [enabled]);

  const checkVersion = useCallback((): void => {
    if (!enabled) return;

    try {
      const buildInfo = getBuildInfo();
      const currentVersion = buildInfo.version;

      if (!currentVersion || currentVersion === "dev") {
        scheduleNextCheck();
        return;
      }

      const storedVersion = getStoredVersion();

      if (storedVersion === null) {
        setStoredVersion(currentVersion);
        scheduleNextCheck();
        return;
      }

      if (currentVersion !== storedVersion) {
        setStoredVersion(currentVersion);
        refreshPage();
        return;
      }

      scheduleNextCheck();
    } catch (error) {
      scheduleNextCheck();
    }
  }, [
    enabled,
    getStoredVersion,
    setStoredVersion,
    refreshPage,
    scheduleNextCheck
  ]);

  useEffect(() => {
    if (!enabled) return;

    checkVersion();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [enabled, checkVersion]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    checkVersion: () => checkVersion()
  };
};
